/**
 * CommandPage — CEO dashboard. Aggregates stats, sparkline trends,
 * alerts, and the cross-GI activity feed.
 */

import { useTranslation } from 'react-i18next';

import { ErrorState } from '@/components/ErrorState';
import { SkeletonCard } from '@/components/LoadingState';
import { RecentActivity } from '@/features/vault/RecentActivity';
import { AlertsList } from '@/features/command/AlertsList';
import { EngineTelemetryPanel } from '@/features/command/EngineTelemetryPanel';
import { Sparkline } from '@/features/command/Sparkline';

import { useCommandSnapshot } from '@/hooks/useCommand';
import { useLocaleStore } from '@/stores/localeStore';
import { formatNumber } from '@/utils/format';

export function CommandPage() {
  const { t } = useTranslation(['command', 'common']);
  const locale = useLocaleStore((s) => s.locale);
  const query = useCommandSnapshot();

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeading />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeading />
        <ErrorState
          title={t('errors.title', { ns: 'errors' })}
          body={(query.error as Error).message}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const snap = query.data;

  return (
    <div className="flex flex-col gap-8">
      <PageHeading />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          value={formatNumber(snap.stats.runs_today, locale)}
          label={t('stats.runs_today')}
          trend={snap.trends.runs}
        />
        <StatCard
          value={`$${formatNumber(snap.stats.revenue_today_ils, locale)}`}
          label={t('stats.revenue_today')}
          trend={snap.trends.revenue_ils}
        />
        <StatCard
          value={formatNumber(snap.stats.active_gis, locale)}
          label={t('stats.active_gis')}
        />
        <StatCard
          value={formatNumber(snap.stats.attestations_today, locale)}
          label={t('stats.attestations_today')}
          trend={snap.trends.attestations}
        />
      </div>

      <EngineTelemetryPanel />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section aria-labelledby="alerts-heading" className="flex flex-col gap-3">
          <h2
            id="alerts-heading"
            className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
          >
            {t('sections.alerts')}
          </h2>
          <AlertsList alerts={snap.alerts} />
        </section>

        <section aria-labelledby="activity-heading" className="flex flex-col gap-3">
          <h2
            id="activity-heading"
            className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
          >
            {t('sections.activity')}
          </h2>
          <RecentActivity events={snap.activity} />
        </section>
      </div>
    </div>
  );
}

function PageHeading() {
  const { t } = useTranslation('command');
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('title')}</h1>
      <p className="mt-1 text-sm text-ink-muted">{t('subtitle')}</p>
    </div>
  );
}

function StatCard({
  value,
  label,
  trend,
}: {
  value: string;
  label: string;
  trend?: number[];
}) {
  const { t } = useTranslation('command');
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-5 shadow-subtle">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="font-mono text-3xl font-semibold tabular-nums text-ink">
            {value}
          </span>
          <span className="text-xs uppercase tracking-wider text-ink-muted">{label}</span>
        </div>
      </div>
      {trend ? (
        <div className="flex items-end justify-between gap-2">
          <Sparkline values={trend} />
          <span className="text-[10px] text-ink-subtle">{t('stats.spark_days')}</span>
        </div>
      ) : null}
    </div>
  );
}
